import { GetServerSideProps, NextPage } from 'next'
import nextI18nextConfig from '~/next-i18next.config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { DependencyInjector } from '~/injector/dependency.injector'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { ApiTrophyResponseInterface } from '~/modules/Trophies/Application/ApiTrophyResponseInterface'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { ProfileRepositoryInterface } from '~/modules/Profile/ProfileRepositoryInterface'
import { GetRelatedTrophies } from '~/modules/Trophies/Application/GetRelatedTrophies'
import { GetTrophy } from '~/modules/Trophies/Application/GetTrophy'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'
import { ApplicationTrophyDto } from '~/modules/Products/Application/Dtos/ApplicationTrophyDto'
import { getServerSession } from 'next-auth/next'
import { authOptions } from '~/pages/api/auth/[...nextauth]'
import { MarketTrophyDetailPage } from '~/components/MarketPages/TrophyDetailPage'
import { fromTrophyDetailPagePropsToViewModel } from '~/modules/PresentationAdapters/TrophyDetailPageAdapter'
import { UserProfile } from '~/modules/Profile/UserProfile'

export interface TrophyDetailsPageProps {
  htmlPageMetaContextProps: HtmlPageMetaContextProps
  trophy: ApiTrophyResponseInterface
  relatedTrophies: ApplicationTrophyDto[]
  ownerProfile?: UserProfile | null
  language: string
  owner: boolean
  backlink?: boolean
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const { trophyId, backlink } = context.query
  const translationsPromise = serverSideTranslations(locale, [
    'user_tos',
    'app_menu',
    'footer',
    'market_banner',
    'trophy_details',
    'related_trophies',
    'trophies',
    'trophy_memories',
    'wallet_error_modal',
    'backlink',
  ])
  const trophyRepository: TrophyRepositoryInterface = DependencyInjector.getInstance()
    .resolve('TrophyRepositoryInterface')
  const profileRepository: ProfileRepositoryInterface = DependencyInjector.getInstance()
    .resolve('ProfileRepositoryInterface')

  if (trophyId) {
    try {
      const [session, relatedTrophies, trophy, ownerProfile] = await Promise.all([
        getServerSession(context.req, context.res, authOptions),
        (new GetRelatedTrophies(trophyRepository)).run(trophyId.toString()),
        (new GetTrophy(trophyRepository)).run(trophyId.toString(), true),
        profileRepository.getByTrophyId(trophyId.toString()),
      ])

      const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

      let owner = false

      if (session && session.user) {
        owner = session.user.id === trophy.userId
      }

      return {
        props: {
          htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
          trophy: JSON.parse(JSON.stringify(trophy)),
          relatedTrophies: JSON.parse(JSON.stringify(relatedTrophies)),
          ownerProfile: JSON.parse(JSON.stringify(ownerProfile)),
          language: locale,
          owner,
          ...(backlink !== undefined && { backlink: true }),
          ...await translationsPromise,
        },
      }
    } catch (exception: unknown) {
      console.error(exception)
    }
  }

  return {
    notFound: true,
  }
}

const TrophyDetailsRoutePage: NextPage<TrophyDetailsPageProps> = (props) => {
  return <MarketTrophyDetailPage viewModel={ fromTrophyDetailPagePropsToViewModel(props) } />
}

export default TrophyDetailsRoutePage
